// Globals
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  getBannersList,
  reorderBanners,
  selectQuery,
  bannersSelector,
  changePosition,
  deleteBanner,
  getBanner,
  resetBanner,
} from '../../feature';

// Components
import {
  StyledCreateButton,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledTabsWrap,
  StyledPageWrapper,
  StyledContentBlock,
  StyledTitleModal,
  StyledButtonsWrapper,
} from './BannersListContainerStyles';
import { Button, Drawer } from '@/ui';
import { Modal, ModalSize, useForm } from '@/components';
import { PlusCircleIcon } from '@/icons';
import { BannersList } from '../../components';
import CreateBannerContainer from '../CreateBannerContainer/CreateBannerContainer';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { IBanner } from '@/models/banners.model';

// Hooks | Helpers
import { useBoolean, useModal } from '@/hooks';
import { GetBannersListDTO, ReorderBannersDTO } from '../../dtos';

const BannersListContainer = () => {
  const { t } = useTranslation(['banners']);
  const { canManage } = usePermissions();
  const canManageBanners = canManage(Resource.BANNER);
  const dispatch = useAppDispatch();
  const [currentBanner, setCurrentBanner] = useState<IBanner | null>(null);
  const { value: bannerFormOpened, setFalse: closeBannerForm, setTrue: openBannerForm } = useBoolean();
  const { isOpened, openModal, closeModal } = useModal();
  const { isLoading, bannersList } = useAppState(bannersSelector);
  const query = useAppState(selectQuery);
  const [form] = useForm();

  const onDrawerClose = () => {
    closeBannerForm();
    dispatch(resetBanner());
    form.resetFields();
  };

  const getBannersListHandler = () => {
    const dto = new GetBannersListDTO({ ...query });
    dispatch(getBannersList(dto));
  };

  const handleEditClick = async (banner: IBanner) => {
    await form.resetFields();
    setCurrentBanner(banner);
    dispatch(getBanner(banner?.id));
    openBannerForm();
  };

  const handleDelete = async () => {
    try {
      if (currentBanner) await dispatch(deleteBanner(currentBanner.id));
      getBannersListHandler();
      closeModal();
      setCurrentBanner(null);
    } catch {}
  };

  const changeBannerForDelete = (banner: IBanner) => {
    setCurrentBanner(banner);
    openModal();
  };

  const handleCloseModal = () => {
    closeModal();
    setCurrentBanner(null);
  };

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (!canManageBanners) return;
      const { active, over } = event;
      if (!bannersList || !over || active.id === over?.id) return;
      const oldIndex = bannersList.data.findIndex((m) => m.id === active.id);
      const newIndex = bannersList.data.findIndex((m) => m.id === over?.id);
      const newPoints = arrayMove(bannersList.data, oldIndex, newIndex).map((m, index) => ({
        ...m,
        position: index + 1,
      }));
      dispatch(changePosition(newPoints));
      const dto = new ReorderBannersDTO(newPoints);
      try {
        await dispatch(reorderBanners(dto));
      } catch {
        dispatch(changePosition(bannersList.data));
      }
    },
    [bannersList, dispatch, canManageBanners],
  );

  useEffect(() => {
    getBannersListHandler();
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('banners_title')}</StyledPageTitle>
        <StyledTabsWrap>
          {canManageBanners && (
            <StyledCreateButton onClick={openBannerForm}>
              <PlusCircleIcon />
              {t('add_banner')}
            </StyledCreateButton>
          )}
        </StyledTabsWrap>
      </StyledHeaderBlock>
      <StyledContentBlock>
        <BannersList
          bannersList={bannersList?.data as IBanner[]}
          isLoading={isLoading}
          handleDragEnd={handleDragEnd}
          setBannerForDelete={changeBannerForDelete}
          handleEditClick={handleEditClick}
          canManage={canManageBanners}
        />
      </StyledContentBlock>
      <Drawer title={t('add_banner')} width={'calc(100vw - 18rem)'} open={bannerFormOpened} onClose={onDrawerClose}>
        <CreateBannerContainer form={form} onFormSubmit={onDrawerClose} isOpen={bannerFormOpened} />
      </Drawer>
      <Modal visible={isOpened} onCancel={handleCloseModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('banners_delete_title')}</StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={handleCloseModal} loading={isLoading} type="primary">
            {t('banners_delete_back')}
          </Button>
          <Button onClick={handleDelete} loading={isLoading} type="text">
            {t('banners_delete')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
    </StyledPageWrapper>
  );
};

export default BannersListContainer;
